import {
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  isValid,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
} from "date-fns";

import type { FilterOption } from "./types";

/* ----------- TYPES ----------- */

export type FilterFieldType = "boolean" | "date" | "year" | "options";

export interface ActiveFilterChip {
  /** Grupo (campo) do filtro — usado para remover o filtro inteiro. */
  group: string;
  /** Texto exibido no chip. */
  label: string;
}

/* ----------- HELPERS ----------- */

/** Label legível do campo, enviado pelo backend (`{ label, options }`); cai na chave se ausente. */
export const getCampoLabel = (
  filters: Record<string, any> | null | undefined,
  group: string,
): string => filters?.[group]?.label ?? group;

/** Chave estável para uma opção de filtro em listas renderizadas. */
export const getOptionKey = (option: string | number | FilterOption, idx: number): string => {
  if (typeof option === "object" && option !== null) {
    return String(option.id ?? idx);
  }
  return `${String(option)}-${idx}`;
};

/** Conta quantos filtros estão ativos em um conjunto de critérios (ignora a busca textual). */
export const getActiveCountFromCriteria = (
  criteria: Record<string, any> | null | undefined,
): number => {
  if (!criteria) return 0;

  return Object.entries(criteria).reduce((count, [key, value]) => {
    if (key === "search") return count;
    if (Array.isArray(value)) return count + value.length;
    if (typeof value === "object" && value !== null)
      return count + (value.from || value.to ? 1 : 0);
    if (value !== undefined && value !== null && value !== false) return count + 1;
    return count;
  }, 0);
};

/** Conta quantos grupos (campos) estão preenchidos num preset — para resumo na UI. */
export const countPresetFields = (criteria: Record<string, any>): number =>
  Object.entries(criteria).reduce((count, [key, value]) => {
    if (key === "search") return count;
    if (Array.isArray(value)) return value.length > 0 ? count + 1 : count;
    if (typeof value === "object" && value !== null)
      return value.from || value.to ? count + 1 : count;
    if (value !== undefined && value !== null && value !== false) return count + 1;
    return count;
  }, 0);

/* ----------- ATALHOS DE DATA ----------- */

export interface DatePreset {
  key: string;
  label: string;
}

export const DATE_PRESETS: DatePreset[] = [
  { key: "hoje", label: "Hoje" },
  { key: "7dias", label: "Últimos 7 dias" },
  { key: "mes", label: "Este mês" },
  { key: "ano", label: "Este ano" },
];

/** Calcula o range ISO (`from`/`to`) de um atalho de data. */
export function getDatePresetRange(key: string): { from: string; to: string } {
  const now = new Date();
  switch (key) {
    case "7dias":
      return { from: startOfDay(subDays(now, 6)).toISOString(), to: endOfDay(now).toISOString() };
    case "mes":
      return { from: startOfMonth(now).toISOString(), to: endOfMonth(now).toISOString() };
    case "ano":
      return { from: startOfYear(now).toISOString(), to: endOfYear(now).toISOString() };
    case "hoje":
    default:
      return { from: startOfDay(now).toISOString(), to: endOfDay(now).toISOString() };
  }
}

/** Deriva o tipo do campo a partir das suas `options` (lista de valores ou marker de tipo). */
export const getFieldType = (options: unknown): FilterFieldType => {
  if (options === "boolean") return "boolean";
  if (options === "year") return "year";
  if (options === "date") return "date";
  return "options";
};

/** Formata uma data ISO para `dd/MM`, ou `null` se inválida. */
const formatDay = (value: unknown): string | null => {
  if (!value) return null;
  const date = new Date(value as string);
  return isValid(date) ? format(date, "dd/MM") : null;
};

/**
 * Resolve o label legível de um valor de array dentro das opções do campo.
 * Para opções `{ id, nome }` (entidades e enums), usa o `nome`; senão, o próprio valor.
 */
const resolveOptionLabel = (options: unknown, value: unknown): string => {
  if (Array.isArray(options)) {
    const option = options.find((opt) =>
      opt && typeof opt === "object" ? opt.id === value : opt === value,
    );
    if (option && typeof option === "object") return String(option.nome ?? value);
    if (option !== undefined) return String(option);
  }
  return String(value);
};

/**
 * Monta os chips de filtros ativos a partir dos critérios aplicados — um chip por grupo.
 * Função pura (sem JSX/estado) para facilitar testes.
 */
export function buildActiveFilterChips(
  filters: Record<string, any>,
  criteria: Record<string, any>,
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  Object.entries(criteria).forEach(([group, value]) => {
    if (group === "search" || value === undefined || value === null || value === false) return;

    const campo = filters[group];
    const options = campo?.options;
    const type = getFieldType(options);
    const groupLabel = campo?.label ?? group;

    if (type === "boolean") {
      if (value) chips.push({ group, label: groupLabel });
      return;
    }

    if (type === "year") {
      chips.push({ group, label: `${groupLabel}: ${value}` });
      return;
    }

    if (type === "date") {
      const range = value as { from?: string; to?: string };
      const from = formatDay(range.from);
      const to = formatDay(range.to);
      if (!from && !to) return;
      const suffix = from && to ? `${from} → ${to}` : from ? `a partir de ${from}` : `até ${to}`;
      chips.push({ group, label: `${groupLabel}: ${suffix}` });
      return;
    }

    // options (array): um único chip por grupo, com os valores resolvidos e juntados.
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      const labels = value.map((v) => resolveOptionLabel(options, v));
      chips.push({ group, label: `${groupLabel}: ${labels.join(", ")}` });
    }
  });

  return chips;
}
